import { useState } from "react";
type Line = {
  description: string;
  costCodeId: string;
  net: string;
  vat: string;
  gross: string;
};
export function PayableLines({
  lines,
  codes,
  editable,
  disabled,
  onSave,
}: {
  lines: any[];
  codes: any[];
  editable: boolean;
  disabled: boolean;
  onSave: (lines: Line[]) => void;
}) {
  const [rows, setRows] = useState<Line[]>(() =>
    lines.map((l) => ({
      description: l.description,
      costCodeId: l.cost_code_id,
      net: l.net_amount,
      vat: l.vat_amount,
      gross: l.gross_amount,
    })),
  );
  const change = (index: number, key: keyof Line, value: string) =>
    setRows((previous) =>
      previous.map((r, i) => (i === index ? { ...r, [key]: value } : r)),
    );
  return (
    <section className="panel">
      <h3>Invoice line allocation</h3>
      <p>
        Split this invoice between finance codes within its saved schedule,
        funding treatment and job. Line totals must equal the invoice exactly.
        The original invoice stays linked to these allocations.
      </p>
      {rows.map((r, i) => (
        <fieldset key={i} disabled={!editable || disabled}>
          <legend>Line {i + 1}</legend>
          <div className="form-grid">
            <label>
              Description
              <input
                value={r.description}
                maxLength={2000}
                onChange={(e) => change(i, "description", e.target.value)}
              />
            </label>
            <label>
              Finance code
              <select aria-label="Finance code"
                value={r.costCodeId}
                onChange={(e) => change(i, "costCodeId", e.target.value)}
              >
                <option value="">Choose code</option>
                {codes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.code} {c.name}
                  </option>
                ))}
              </select>
            </label>
            {(["net", "vat", "gross"] as const).map((k) => (
              <label key={k}>
                {k === "vat" ? "VAT" : k === "net" ? "Net" : "Gross"}
                <input
                  inputMode="decimal"
                  value={r[k]}
                  onChange={(e) => change(i, k, e.target.value)}
                />
              </label>
            ))}
          </div>
          {editable && (
            <button
              type="button"
              disabled={disabled}
              onClick={() =>
                setRows((previous) => previous.filter((_, n) => n !== i))
              }
            >
              Remove line {i + 1}
            </button>
          )}
        </fieldset>
      ))}
      {editable && (
        <div className="actions">
          <button
            type="button"
            disabled={disabled || rows.length >= 100}
            onClick={() =>
              setRows((previous) => [
                ...previous,
                {
                  description: "",
                  costCodeId: "",
                  net: "",
                  vat: "",
                  gross: "",
                },
              ])
            }
          >
            Add invoice line
          </button>
          <button
            type="button"
            disabled={disabled || !rows.length}
            onClick={() => onSave(rows)}
          >
            Save line allocations
          </button>
        </div>
      )}
    </section>
  );
}
